import { createDebugLogger } from '../logging/DebugLogger';

const log = createDebugLogger('lru');

type ValueFactory<K, V> = (key: K) => Promise<V>;

/**
 * LRU cache with asynchronous value factory.
 *
 * @param K - cache key type (for example, `string`).
 * @param V - cache entry type (likely, some class or an interface).
 */
class AsyncLRUCache<K, V> {
  private readonly store = new Map<K, V>();
  private readonly counters = new Map<K, number>();

  /**
   * @param maxEntries - Maximum number of entries in cache.
   * @param createEntry - Factory function that creates values for missing keys
   * on cache misses.
   */
  constructor(
    private readonly maxEntries: number,
    private readonly createEntry: ValueFactory<K, V>
  ) {
    if (maxEntries < 0) {
      throw new TypeError('Invalid argument');
    }
  }

  /**
   * Returns an entry from the cache.
   *
   * If the entry is found in cache, its usage count is incremented. If no entry
   * is found in cache by the given key, a new value is created using
   * {@link AsyncLRUCache.createEntry} factory, and put in cache after that.
   * If there's no space left in cache to store a new entry, the least used
   * entry is evicted from cache.
   *
   * @param key - Key to find or create value by.
   * @returns Promise that resolves to the cached value.
   */
  public async getEntry(key: K): Promise<V> {
    let entry = this.store.get(key);

    if (entry !== undefined) {
      const previousUsageCount = this.counters.get(key);
      if (previousUsageCount) this.counters.set(key, previousUsageCount + 1);
    } else {
      log('cache miss: %s', key);
      entry = await this.createEntry(key);
      this.putInStore(key, entry);
    }

    return entry;
  }

  /**
   * Removes all entries from the cache.
   */
  public clearEntries(): void {
    this.store.clear();
    this.counters.clear();
    log('cache cleared');
  }

  private putInStore(key: K, value: V) {
    if (this.store.size === this.maxEntries) {
      let minUsedKey = key;
      let minUsedVal = Number.MAX_SAFE_INTEGER;

      this.counters.forEach((v, k) => {
        if (v < minUsedVal) {
          minUsedVal = v;
          minUsedKey = k;
        }
      });

      if (key !== minUsedKey) {
        log('cache eviction: %s', minUsedKey);
        this.counters.delete(minUsedKey);
        this.store.delete(minUsedKey);
      }
    }

    if (this.store.size < this.maxEntries) {
      this.store.set(key, value);
      this.counters.set(key, 1);
    }
  }
}

export default AsyncLRUCache;
